import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail, Phone, Users } from "lucide-react";
import { API } from "@/config";
import { Seo } from "@/components/Seo";
import { SectionHeading } from "@/components/SectionHeading";
import { extractApiError } from "@/utils/formatters";

function CareerPage({ siteConfig }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    birthdate: "",
    has_criminal_record: null,
    has_education: null,
    qualifications: [],
    other_qualifications: "",
    earliest_start: "",
    work_type: "",
    experience: "",
    motivation: "",
    consent: false,
  });
  const [formState, setFormState] = useState({ loading: false, success: false, error: "" });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleQualificationToggle = (qual) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: prev.qualifications.includes(qual)
        ? prev.qualifications.filter((q) => q !== qual)
        : [...prev.qualifications, qual],
    }));
  };

  const isStep1Valid = formData.name && formData.email && formData.phone && formData.address && formData.birthdate && formData.has_criminal_record !== null && formData.has_education !== null;
  const isStep2Valid = formData.earliest_start && formData.work_type && formData.consent;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isStep2Valid) return;

    setFormState({ loading: true, success: false, error: "" });
    try {
      await axios.post(`${API}/applications`, formData);
      setFormState({ loading: false, success: true, error: "" });
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        birthdate: "",
        has_criminal_record: null,
        has_education: null,
        qualifications: [],
        other_qualifications: "",
        earliest_start: "",
        work_type: "",
        experience: "",
        motivation: "",
        consent: false,
      });
      setStep(1);
    } catch (error) {
      setFormState({
        loading: false,
        success: false,
        error: extractApiError(error, "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."),
      });
    }
  };

  const benefits = [
    "Vollzeit, Teilzeit oder Minijob möglich",
    "Individuelle Arbeitszeitmodelle",
    "Attraktive Vergütung",
    "Kollegiales Team",
    "Flache Hierarchien",
    "Quereinsteiger willkommen!",
  ];

  const requirements = [
    "Flexibilität und Zuverlässigkeit",
    "Freundliches Auftreten",
    "Positive Arbeitshaltung",
    "Freude an Teamarbeit",
    "Stärken in Kommunikation",
    "Offen für Herausforderungen",
  ];

  return (
    <>
      <Seo
        path="/karriere"
        title="Karriere – Jobs im Sicherheitsdienst"
        description="Werden Sie Teil von Deutsche Bewachung & Sicherheit. Wir suchen engagierte Mitarbeiter für Objektschutz, Revierdienst und Veranstaltungssicherheit in Hannover und Umgebung."
        keywords="Jobs Sicherheitsdienst Hannover, Karriere Sicherheitsfirma, Stelle Objektschutz, Sicherheitsmitarbeiter Hannover"
      />
      <section className="page-hero page-hero--dark career-hero">
        <div className="container page-hero__content">
          <span className="section-eyebrow">Karriere</span>
          <h1>Werden Sie Teil von Deutsche B&S – Sicherheit mit Zukunft.</h1>
          <p>
            Wir suchen engagierte Mitarbeiter für die Objektüberwachung in Hannover und Umgebung.
            Starten Sie Ihre Karriere im Sicherheitsdienst.
          </p>
        </div>
      </section>

      <section className="section section--light">
        <div className="container">
          <div className="career-benefits-grid">
            <div className="career-benefits-card career-benefits-card--dark">
              <h3>Das bieten wir Ihnen...</h3>
              <ul className="career-list">
                {benefits.map((benefit) => (
                  <li key={benefit}>
                    <CheckCircle2 size={20} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="career-benefits-card">
              <h3>...und das bringen Sie mit</h3>
              <ul className="career-list career-list--requirements">
                {requirements.map((req) => (
                  <li key={req}>
                    <Users size={20} />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--light-alt" id="bewerbung">
        <div className="container">
          <SectionHeading
            eyebrow="Jetzt bewerben"
            title="Bewerbung für Objektüberwachung"
            text="Füllen Sie das Formular aus und wir melden uns bei Ihnen. Unkompliziert und schnell."
          />

          {formState.success ? (
            <div className="application-success">
              <CheckCircle2 size={48} />
              <h3>Vielen Dank für Ihre Bewerbung!</h3>
              <p>Wir haben Ihre Unterlagen erhalten und werden uns zeitnah bei Ihnen melden.</p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setFormState({ loading: false, success: false, error: "" })}
              >
                Weitere Bewerbung einreichen
              </button>
            </div>
          ) : (
            <div className="application-form-container">
              <div className="application-steps">
                <div className={`application-step ${step >= 1 ? "application-step--active" : ""}`}>
                  <span className="application-step__number">1</span>
                  <span className="application-step__label">Persönliche Daten</span>
                </div>
                <div className="application-step__line" />
                <div className={`application-step ${step >= 2 ? "application-step--active" : ""}`}>
                  <span className="application-step__number">2</span>
                  <span className="application-step__label">Verfügbarkeit & Motivation</span>
                </div>
              </div>

              <form className="application-form" onSubmit={handleSubmit}>
                {step === 1 && (
                  <div className="application-form__step">
                    <div className="form-row">
                      <label className="form-field">
                        <span className="form-label">Name <span className="required">*</span></span>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          placeholder="Vor- und Nachname"
                          required
                        />
                      </label>
                      <label className="form-field">
                        <span className="form-label">E-Mail <span className="required">*</span></span>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="ihre@email.de"
                          required
                        />
                      </label>
                    </div>

                    <div className="form-row">
                      <label className="form-field">
                        <span className="form-label">Telefon <span className="required">*</span></span>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          placeholder="Ihre Telefonnummer"
                          required
                        />
                      </label>
                      <label className="form-field">
                        <span className="form-label">Geburtsdatum <span className="required">*</span></span>
                        <input
                          type="date"
                          value={formData.birthdate}
                          onChange={(e) => handleChange("birthdate", e.target.value)}
                          required
                        />
                      </label>
                    </div>

                    <label className="form-field">
                      <span className="form-label">Adresse <span className="required">*</span></span>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleChange("address", e.target.value)}
                        placeholder="Straße, Hausnummer, PLZ, Ort"
                        required
                      />
                    </label>

                    <div className="form-field">
                      <span className="form-label">Haben Sie relevante Eintragungen im Strafregister? <span className="required">*</span></span>
                      <div className="radio-group">
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="criminal_record"
                            checked={formData.has_criminal_record === true}
                            onChange={() => handleChange("has_criminal_record", true)}
                          />
                          <span>Ja</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="criminal_record"
                            checked={formData.has_criminal_record === false}
                            onChange={() => handleChange("has_criminal_record", false)}
                          />
                          <span>Nein</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-field">
                      <span className="form-label">Haben Sie eine abgeschlossene Berufsausbildung/Studium? <span className="required">*</span></span>
                      <div className="radio-group">
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="education"
                            checked={formData.has_education === true}
                            onChange={() => handleChange("has_education", true)}
                          />
                          <span>Ja</span>
                        </label>
                        <label className="radio-option">
                          <input
                            type="radio"
                            name="education"
                            checked={formData.has_education === false}
                            onChange={() => handleChange("has_education", false)}
                          />
                          <span>Nein</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-field">
                      <span className="form-label">Verfügen Sie über berufsspezifische Qualifizierungen?</span>
                      <div className="checkbox-group">
                        <label className="checkbox-option">
                          <input
                            type="checkbox"
                            checked={formData.qualifications.includes("unterweisung_34a")}
                            onChange={() => handleQualificationToggle("unterweisung_34a")}
                          />
                          <span>Unterweisung nach §34a</span>
                        </label>
                        <label className="checkbox-option">
                          <input
                            type="checkbox"
                            checked={formData.qualifications.includes("sachkunde_34a")}
                            onChange={() => handleQualificationToggle("sachkunde_34a")}
                          />
                          <span>Sachkundeprüfung nach §34a</span>
                        </label>
                        <label className="checkbox-option">
                          <input
                            type="checkbox"
                            checked={formData.qualifications.includes("fachkraft")}
                            onChange={() => handleQualificationToggle("fachkraft")}
                          />
                          <span>Fachkraft für Schutz und Sicherheit</span>
                        </label>
                      </div>
                    </div>

                    <label className="form-field">
                      <span className="form-label">Sonstige berufsspezifische Qualifizierungen</span>
                      <input
                        type="text"
                        value={formData.other_qualifications}
                        onChange={(e) => handleChange("other_qualifications", e.target.value)}
                        placeholder="z.B. Erste-Hilfe-Kurs, Führerschein, etc."
                      />
                    </label>

                    <button
                      type="button"
                      className="button button--primary button--full"
                      onClick={() => setStep(2)}
                      disabled={!isStep1Valid}
                    >
                      Weiter zu Verfügbarkeit & Motivation
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div className="application-form__step">
                    <div className="form-row">
                      <label className="form-field">
                        <span className="form-label">Frühester Eintrittstermin <span className="required">*</span></span>
                        <input
                          type="date"
                          value={formData.earliest_start}
                          onChange={(e) => handleChange("earliest_start", e.target.value)}
                          required
                        />
                      </label>
                      <label className="form-field">
                        <span className="form-label">Gewünschte Beschäftigungsart <span className="required">*</span></span>
                        <select
                          value={formData.work_type}
                          onChange={(e) => handleChange("work_type", e.target.value)}
                          required
                        >
                          <option value="">Bitte wählen...</option>
                          <option value="vollzeit">Vollzeit</option>
                          <option value="teilzeit">Teilzeit</option>
                          <option value="minijob">Minijob</option>
                        </select>
                      </label>
                    </div>

                    <label className="form-field">
                      <span className="form-label">Berufserfahrung im Sicherheitsbereich</span>
                      <textarea
                        value={formData.experience}
                        onChange={(e) => handleChange("experience", e.target.value)}
                        placeholder="Beschreiben Sie kurz Ihre bisherige Erfahrung (falls vorhanden)"
                        rows={3}
                      />
                    </label>

                    <label className="form-field">
                      <span className="form-label">Warum möchten Sie bei Deutsche B&S arbeiten?</span>
                      <textarea
                        value={formData.motivation}
                        onChange={(e) => handleChange("motivation", e.target.value)}
                        placeholder="Ihre Motivation in wenigen Sätzen"
                        rows={4}
                      />
                    </label>

                    <label className="checkbox-option consent-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => handleChange("consent", e.target.checked)}
                        required
                      />
                      <span>
                        Ich stimme der Verarbeitung meiner Daten gemäß der Datenschutzerklärung zu. <span className="required">*</span>
                      </span>
                    </label>

                    {formState.error && <div className="form-feedback error">{formState.error}</div>}

                    <div className="form-actions">
                      <button
                        type="button"
                        className="button button--secondary"
                        onClick={() => setStep(1)}
                      >
                        Zurück
                      </button>
                      <button
                        type="submit"
                        className="button button--primary"
                        disabled={formState.loading || !isStep2Valid}
                      >
                        {formState.loading ? "Wird gesendet..." : "Bewerbung absenden"}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </section>

      <section className="section section--dark">
        <div className="container">
          <SectionHeading
            eyebrow="Noch Fragen?"
            title="Wir sind für Sie da"
            text="Bei Fragen zu Ihrer Bewerbung oder offenen Stellen erreichen Sie uns jederzeit."
            light
          />
          <div className="career-contact-grid">
            <a href={`tel:+49${siteConfig.contact.phone.replace(/^0/, "")}`} className="career-contact-card">
              <Phone size={24} />
              <span>{siteConfig.contact.phone}</span>
            </a>
            <a href={`mailto:${siteConfig.contact.email}`} className="career-contact-card">
              <Mail size={24} />
              <span>{siteConfig.contact.email}</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export { CareerPage };
